// =====================================================================
// /admin/presence - Tableau de bord prezenta (Faza 3.0)
// =====================================================================
// Functionalitati:
// - Matrix view: useri x zile (saptamana), cu etichete colorate per cell
// - Click cell -> modal cu actiuni:
//     - Set type principal (13 etichete, full day = 7h36)
//     - Split workflow: TRAV existent + adauga MAL/REC/CSSO/AA cu durata
//     - Supprimer entry
// - Bulk default "Tous TRAV" pentru ziua curenta (Q21a)
// - Permission gating: presence.create.all / .update.all / .delete.all / .bulk_default.all
// =====================================================================

import { useState, useEffect, useMemo, Fragment } from 'react';
import { attendance as attendanceApi, users as usersApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { trierPersonnes } from '../../lib/tri-personnes.js';
import { aujourdhuiBruxelles } from '../../lib/dates.js';
import { ficheReservee } from '../../lib/roles.js';

// ---------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------

const TYPE_LABELS_FR = {
  TRAV:  'Travail',
  MAL:   'Maladie',
  REC:   'Récupération',
  AA:    'Abs. Autorisée',
  CSSO:  'Congé sans solde',
  JF:    'Jour Férié',
  VA:    'Congé Légaux',
  HC:    'Hors contrat',
  PAR:   'Parental',
  MAT:   'Maternité',
  PAT:   'Paternité',
  PER:   'Personnel',
  AUTRE: 'Autre',
  'VA-0.5': 'Vacances (1/2j)',
  CC:    'Circonstance',
  JFR:   'Férié récupéré',
};

const TYPE_COLORS = {
  TRAV:  { bg: '#dcfce7', fg: '#166534' },
  MAL:   { bg: '#fee2e2', fg: '#991b1b' },
  AA:    { bg: '#fef3c7', fg: '#92400e' },
  CSSO:  { bg: '#e0e7ff', fg: '#3730a3' },
  JF:    { bg: '#fce7f3', fg: '#9f1239' },
  VA:    { bg: '#dbeafe', fg: '#1e40af' },
  REC:   { bg: '#f3e8ff', fg: '#6b21a8' },
  HC:    { bg: '#f3f4f6', fg: '#374151' },
  PAR:   { bg: '#cffafe', fg: '#155e75' },
  MAT:   { bg: '#fdf4ff', fg: '#86198f' },
  PAT:   { bg: '#ecfeff', fg: '#0e7490' },
  PER:   { bg: '#fffbeb', fg: '#854d0e' },
  AUTRE: { bg: '#f5f5f4', fg: '#44403c' },
  'VA-0.5': { bg: '#e0f2fe', fg: '#075985' },
  CC:    { bg: '#ffe4e6', fg: '#9f1239' },
  JFR:   { bg: '#fef9c3', fg: '#854d0e' },
};

const DAY_NAMES_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const STANDARD_DAY_MIN = 456; // 7h36
const FULL_DAY_TYPES = ['TRAV', 'MAL', 'REC', 'AA', 'HC', 'PAT', 'MAT', 'VA', 'CC', 'JFR'];
const PARTIAL_TYPES = ['MAL', 'REC', 'AA', 'HC', 'VA-0.5'];
const VA_HALF_DURATION_MIN = 228; // 3h48 = jumate de zi

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

function formatDuration(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${m.toString().padStart(2, '0')}`;
}

function dateToISO(date) {
  return date.toISOString().slice(0, 10);
}

function getMonday(date) {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0); // noon to avoid TZ DST issues
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

// « Aujourd'hui » à Bruxelles, comme le serveur (lib/dates.js) : entre minuit et 2 h,
// la date UTC vaut encore la veille — « Tous TRAV » pointait le mauvais jour.
function todayISO() {
  return aujourdhuiBruxelles();
}

// ---------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------

function headStyle(width, align) {
  return {
    width,
    padding: '8px 6px',
    borderBottom: '2px solid #d1d5db',
    background: '#f9fafb',
    textAlign: align,
    fontWeight: 600,
    fontSize: 12,
  };
}

function tdStyle(align) {
  return {
    border: '1px solid #e5e7eb',
    padding: '6px 8px',
    textAlign: align,
    verticalAlign: 'top',
  };
}

// ---------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------

export default function Presence({ embedded = false }) {
  const { can, user: moi } = useAuth();
  const canRead = can('presence.read.all');
  const canCreate = can('presence.create.all');
  const canUpdate = can('presence.update.all');
  const canDelete = can('presence.delete.all');
  const canBulk = can('presence.bulk_default.all');
  const canMask = can('users.update.all');  // Day 14 — masquer/afficher collaborateur

  const [viewMode, setViewMode] = useState('week'); // 'today' | 'week'
  // Semaine du jour À BRUXELLES (comme « Aujourd'hui » et « Tous TRAV »).
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date(todayISO() + 'T12:00:00')));
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalCell, setModalCell] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showMasked, setShowMasked] = useState(false);  // Day 14

  const startISO = viewMode === 'today' ? todayISO() : dateToISO(weekStart);
  const endISO   = viewMode === 'today' ? todayISO() : dateToISO(addDays(weekStart, 6));

  const loadMatrix = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await attendanceApi.matrix(startISO, endISO, showMasked);
      setMatrix(data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMatrix(); }, [startISO, endISO, showMasked]);

  const toggleMask = async (u) => {
    // CDD : le DÉMASQUAGE exige une nouvelle période de contrat (garde 17-08-2026)
    let body = {};
    if (u.excludeFromPresence && (u.contractType === 'CDD' || u.contractType === 'INTERIM')) {
      const cs = window.prompt(`${u.name} est en ${u.contractType === 'INTERIM' ? 'intérim' : 'CDD'}.\nNouvelle période de contrat — date DÉBUT (AAAA-MM-JJ) :`, todayISO());
      if (cs === null) return;
      const ce = window.prompt('Date FIN du contrat (AAAA-MM-JJ) :', cs);
      if (ce === null) return;
      body = { contractStart: cs.trim(), contractEnd: ce.trim() };
      // Férié dans la période ? → proposer la découpe en 2 contrats (18-08-2026)
      try {
        const chk = await usersApi.contractFerieCheck(body.contractStart, body.contractEnd);
        if (chk.feries?.length) {
          const fList = chk.feries.map(f => `${f.name} (${f.date})`).join(', ');
          const segTxt = (chk.split || []).map(s => `${s.start} → ${s.end}`).join('  +  ');
          body.splitAroundFerie = window.confirm(
            `⚠️ La période contient un jour férié : ${fList}.\n\nScinder en ${chk.split?.length || 2} contrats pour ne pas payer le férié (~200 €) ?\n${segTxt}\n\nOK = scinder · Annuler = garder 1 seul contrat (férié payé)`
          );
        }
      } catch (_) { /* check best-effort */ }
      const motif = window.prompt('Motif de la demande (optionnel — apparaîtra dans le mail contrats) :', '');
      if (motif === null) return;
      if (motif.trim()) body.motif = motif.trim();
    }
    try { await usersApi.toggleMask(u.id, body); loadMatrix(); }
    catch (e) { alert('Erreur: ' + (e.response?.data?.message || e.message)); }
  };

  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));
  const goToWeek = (e) => {
    if (!e.target.value) return;
    setWeekStart(getMonday(new Date(e.target.value)));
  };

  const handleBulkToday = async () => {
    const today = todayISO();
    if (!confirm(`Marquer TRAV (7h36) pour tous les utilisateurs sans présence le ${today} ?`)) return;
    setBulkLoading(true);
    try {
      const r = await attendanceApi.bulkDefaultTrav({ date: today });
      alert(`OK: ${r.added} ajoutés, ${r.skipped} ignorés (déjà présents) sur ${r.total} utilisateurs`);
      loadMatrix();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.message || e.message));
    } finally {
      setBulkLoading(false);
    }
  };

  const usersByRole = useMemo(() => {
    if (!matrix) return [];
    const groups = {};
    for (const u of matrix.users) {
      if (!groups[u.role]) groups[u.role] = [];
      groups[u.role].push(u);
    }
    // Les roles restent des sections (l'ecran se lit role par role) ; a
    // l'interieur de chacune, les personnes sont par ordre alphabetique.
    return Object.entries(groups).map(([role, liste]) => [role, trierPersonnes(liste)]);
  }, [matrix]);

  if (!canRead) {
    return <div className="page"><div className="alert alert-error">Accès refusé</div></div>;
  }

  return (
    <div className={embedded ? '' : 'page'} style={embedded ? { padding: 0 } : undefined}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        {!embedded && <h1 style={{ margin: 0 }}>Présence</h1>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
          {/* View mode toggle (Aujourd'hui / Semaine) */}
          <div style={{ display: 'inline-flex', gap: 0, background: '#f1f5f9', padding: 2, borderRadius: 6 }}>
            <button
              onClick={() => setViewMode('today')}
              style={{ padding: '5px 12px', border: 0, borderRadius: 4, background: viewMode === 'today' ? 'white' : 'transparent', color: viewMode === 'today' ? '#1e293b' : '#64748b', fontSize: 12, fontWeight: viewMode === 'today' ? 600 : 400, cursor: 'pointer', boxShadow: viewMode === 'today' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}
            >📅 Aujourd'hui</button>
            <button
              onClick={() => setViewMode('week')}
              style={{ padding: '5px 12px', border: 0, borderRadius: 4, background: viewMode === 'week' ? 'white' : 'transparent', color: viewMode === 'week' ? '#1e293b' : '#64748b', fontSize: 12, fontWeight: viewMode === 'week' ? 600 : 400, cursor: 'pointer', boxShadow: viewMode === 'week' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}
            >🗓️ Semaine</button>
          </div>

          {viewMode === 'week' && (
            <>
              <button className="btn btn-sm" onClick={prevWeek} type="button">‹ Sem. préc.</button>
              <input type="date" value={dateToISO(weekStart)} onChange={goToWeek}
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db' }} />
              <button className="btn btn-sm" onClick={nextWeek} type="button">Sem. suiv. ›</button>
            </>
          )}

          {canBulk && (
            <button className="btn btn-primary" type="button"
              onClick={handleBulkToday} disabled={bulkLoading}>
              {bulkLoading ? 'En cours...' : `Tous TRAV (${todayISO()})`}
            </button>
          )}
          {canMask && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#475569' }}>
              <input type="checkbox" checked={showMasked} onChange={e => setShowMasked(e.target.checked)} />
              Afficher masqués
            </label>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}

      {loading ? <div className="loading" style={{ marginTop: 16 }}>Chargement...</div>
        : !matrix ? <div className="empty" style={{ marginTop: 16 }}>Aucune donnée</div>
        : (
          <div style={{ overflowX: 'auto', marginTop: 16 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={headStyle('260px', 'left')}>Opérateur</th>
                  {matrix.days.map(d => {
                    const bg = d.isHoliday ? '#fce7f3' : d.isWeekend ? '#f3f4f6' : '#f9fafb';
                    return (
                      <th key={d.date}
                        style={{ ...headStyle('110px', 'center'), background: bg }}
                        title={d.holidayName || ''}>
                        <div>{DAY_NAMES_FR[d.dow]} {d.date.slice(8, 10)}</div>
                        {d.isHoliday && <div style={{ fontSize: 10, color: '#9f1239', fontWeight: 700 }}>JF</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {usersByRole.map(([role, usersInRole]) => (
                  <Fragment key={role}>
                    <tr>
                      <td colSpan={matrix.days.length + 1}
                        style={{ background: '#e5e7eb', padding: '4px 8px', fontWeight: 600, fontSize: 12 }}>
                        {role} ({usersInRole.length})
                      </td>
                    </tr>
                    {usersInRole.map(u => (
                      <tr key={u.id} style={(u.isActive === false || u.masked) ? { opacity: 0.55 } : {}}>
                        <td style={tdStyle('left')}>
                          <div style={{ fontWeight: 500 }}>
                            {u.name}
                            {u.masked && (
                              <span style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8', fontWeight: 400, fontStyle: 'italic' }}
                                title={u.repeche ? 'Masqué, affiché ici parce qu\'un contrat terminé couvre cette période ou qu\'il y a des présences. Pour un nouveau contrat : Embauche, ou cochez « Afficher masqués ».' : undefined}>
                                (masqué)
                              </span>
                            )}
                            {u.isActive === false && !u.masked && (
                              <span style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8', fontWeight: 400, fontStyle: 'italic' }}>(inactif)</span>
                            )}
                            {/* Pas d'« Afficher » sur une rangée repêchée CDD / intérim : il y crée une
                                NOUVELLE période et une demande de contrat SD Worx. */}
                            {canMask && !(u.repeche && (u.contractType === 'CDD' || u.contractType === 'INTERIM'))
                              && !ficheReservee(u, moi) && (
                              <button onClick={(e) => { e.stopPropagation(); toggleMask(u); }} title={u.masked ? 'Réafficher partout' : 'Masquer (données conservées)'}
                                style={{ marginLeft: 6, padding: '1px 6px', fontSize: 10, border: '1px solid #d1d5db', borderRadius: 4, background: u.masked ? '#dcfce7' : '#fff', cursor: 'pointer' }}>
                                {u.masked ? '👁 Afficher' : '🚫 Masquer'}
                              </button>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{u.group?.name || '—'}</div>
                        </td>
                        {matrix.days.map(d => {
                          const entries = matrix.cells[u.id]?.[d.date] || [];
                          const cellBg = d.isHoliday ? '#fdf2f8' : d.isWeekend ? '#f9fafb' : '#fff';
                          const clickable = canCreate || canUpdate;
                          // Marquage CDD (17-08-2026, variante B+C validée Silviu) :
                          //   hors contrat → hachuré + « HC » ; dans le contrat → soulignement orange
                          //   CDD successifs: on regarde TOUTES les périodes (historique)
                          const periods = (u.contracts && u.contracts.length)
                            ? u.contracts
                            : (u.contractStart && u.contractEnd ? [{ startDate: u.contractStart, endDate: u.contractEnd }] : []);
                          const isCdd = (u.contractType === 'CDD' || u.contractType === 'INTERIM') && periods.length > 0; // M-011: intérim marqué comme CDD
                          const inContract = isCdd && periods.some(c => d.date >= c.startDate && d.date <= c.endDate);
                          const outContract = isCdd && !inContract;
                          const cddStyle = outContract
                            ? { backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 4px, rgba(100,116,139,.20) 4px 7px)' }
                            : inContract
                              ? { boxShadow: 'inset 0 -3px 0 0 #d97706' }
                              : {};
                          return (
                            <td key={d.date}
                              style={{
                                ...tdStyle('center'),
                                background: cellBg,
                                cursor: clickable ? 'pointer' : 'default',
                                padding: 2,
                                ...cddStyle,
                              }}
                              onClick={() => clickable && setModalCell({ user: u, date: d.date, entries })}>
                              {entries.length === 0
                                ? (outContract
                                    ? <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 10 }}>HC</span>
                                    : <span style={{ color: '#d1d5db' }}>—</span>)
                                : entries.map(att => <AttendanceTag key={att.id} att={att} />)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      {modalCell && (
        <CellModal cell={modalCell}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onClose={() => setModalCell(null)}
          onSaved={() => { setModalCell(null); loadMatrix(); }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// AttendanceTag - tag colorat pe cell
// ---------------------------------------------------------------------

function AttendanceTag({ att }) {
  const c = TYPE_COLORS[att.type] || TYPE_COLORS.AUTRE;
  return (
    <span style={{
      display: 'inline-block',
      background: c.bg,
      color: c.fg,
      padding: '2px 6px',
      borderRadius: 3,
      fontSize: 11,
      fontWeight: 600,
      margin: 1,
      whiteSpace: 'nowrap',
    }} title={att.note || ''}>
      {att.type}
      {att.isPartial && <span style={{ marginLeft: 3, fontWeight: 500 }}>{formatDuration(att.durationMin)}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------
// CellModal - actiuni pentru o celula (user + date)
// ---------------------------------------------------------------------

function CellModal({ cell, canCreate, canUpdate, canDelete, onClose, onSaved }) {
  const { user, date, entries } = cell;
  const hasEntries = entries.length > 0;
  const fullDayEntry = entries.find(e => !e.isPartial);
  const partialEntries = entries.filter(e => e.isPartial);
  const canSplit = fullDayEntry && fullDayEntry.type === 'TRAV' && partialEntries.length === 0;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [splitForm, setSplitForm] = useState(null);

  const handleSetType = async (type) => {
    setError(null);
    setSaving(true);
    try {
      if (fullDayEntry) {
        await attendanceApi.update(fullDayEntry.id, {
          type,
          durationMin: STANDARD_DAY_MIN,
        });
      } else {
        await attendanceApi.create({
          userId: user.id,
          date,
          type,
          durationMin: STANDARD_DAY_MIN,
        });
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!confirm('Supprimer cette entrée ?')) return;
    setError(null);
    setSaving(true);
    try {
      await attendanceApi.remove(entryId);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setSaving(false);
    }
  };

  const startSplit = (partialType) => {
    if (partialType === 'VA-0.5') {
      setSplitForm({ type: partialType, hours: 3, minutes: 48, note: '', locked: true });
      return;
    }
    setSplitForm({ type: partialType, hours: 2, minutes: 0, note: '' });
  };

  const submitSplit = async (e) => {
    e.preventDefault();
    const totalMin = (parseInt(splitForm.hours, 10) || 0) * 60 + (parseInt(splitForm.minutes, 10) || 0);
    if (totalMin < 15) { setError('Durée minimum 15 min'); return; }
    if (totalMin > STANDARD_DAY_MIN) { setError('Durée maximum 7h36'); return; }
    setError(null);
    setSaving(true);
    try {
      await attendanceApi.addPartial({
        userId: user.id,
        date,
        partialType: splitForm.type,
        partialDurationMin: totalMin,
        note: splitForm.note?.trim() || undefined,
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-large" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h2>{user.name} — {date}</h2>
          <button className="btn-close" onClick={onClose} type="button">×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          {/* Existing entries cu Delete */}
          {hasEntries && (
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Entrées actuelles</h3>
              {entries.map(att => (
                <div key={att.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: 8, marginBottom: 4,
                  background: '#f9fafb', borderRadius: 4,
                }}>
                  <AttendanceTag att={att} />
                  <span style={{ flex: 1, fontSize: 12, color: '#374151' }}>
                    {formatDuration(att.durationMin)}
                    {att.isPartial && <span style={{ color: '#a16207' }}> · partiel</span>}
                    {att.note && <span> · {att.note}</span>}
                    <span style={{ marginLeft: 4, fontSize: 10, color: '#9ca3af' }}>(source: {att.source})</span>
                  </span>
                  {canDelete && (
                    <button className="btn btn-sm btn-danger" type="button"
                      onClick={() => handleDelete(att.id)}
                      disabled={saving}>
                      Supprimer
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Split form (when active) */}
          {splitForm && (
            <form onSubmit={submitSplit} style={{
              padding: 12,
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: 4,
              marginBottom: 16,
            }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Split avec {TYPE_LABELS_FR[splitForm.type]}</h4>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <label style={{ minWidth: 60 }}>Durée:</label>
                <input type="number" min="0" max="7" required value={splitForm.hours} disabled={splitForm.locked}
                  onChange={e => setSplitForm({ ...splitForm, hours: e.target.value })}
                  style={{ width: 60, padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db' }} />
                <span>h</span>
                <input type="number" min="0" max="59" required value={splitForm.minutes} disabled={splitForm.locked}
                  onChange={e => setSplitForm({ ...splitForm, minutes: e.target.value })}
                  style={{ width: 60, padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db' }} />
                <span>min</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <label style={{ minWidth: 60 }}>Note:</label>
                <input type="text" maxLength={200} value={splitForm.note}
                  onChange={e => setSplitForm({ ...splitForm, note: e.target.value })}
                  placeholder="Optionnel"
                  style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-sm" onClick={() => setSplitForm(null)}>Annuler</button>
                <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                  {saving ? 'En cours...' : 'Confirmer split'}
                </button>
              </div>
            </form>
          )}

          {/* Set type principal sau Split start */}
          {!splitForm && (canCreate || canUpdate) && (
            <>
              <h3 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
                {hasEntries ? 'Modifier le type principal' : 'Définir une étiquette (7h36 par défaut)'}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6, marginBottom: 16 }}>
                {FULL_DAY_TYPES.map(type => { const label = TYPE_LABELS_FR[type];
                  const c = TYPE_COLORS[type];
                  if (!c) return null;
                  const isCurrent = fullDayEntry && fullDayEntry.type === type;
                  return (
                    <button key={type} type="button"
                      onClick={() => handleSetType(type)}
                      disabled={saving}
                      style={{
                        padding: '8px 12px',
                        background: c.bg,
                        color: c.fg,
                        border: isCurrent ? `2px solid ${c.fg}` : '1px solid #e5e7eb',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        textAlign: 'left',
                      }}>
                      <strong>{type}</strong> — {label}
                      {isCurrent && <span style={{ marginLeft: 4 }}>✓</span>}
                    </button>
                  );
                })}
              </div>

              {canSplit && (
                <>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Ajouter une 2e étiquette (split)</h3>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>
                    L&apos;opérateur est en TRAV. Si quelqu&apos;un quitte (ex: maladie 2h), ajoute une étiquette partielle.
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                    {PARTIAL_TYPES.map(t => {
                      const c = TYPE_COLORS[t];
                      if (!c) return null;
                      return (
                        <button key={t} type="button" onClick={() => startSplit(t)}
                          disabled={saving}
                          style={{
                            padding: '6px 12px',
                            background: c.bg,
                            color: c.fg,
                            border: `2px dashed ${c.fg}`,
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}>
                          + {t} ({TYPE_LABELS_FR[t]})
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn" onClick={onClose} type="button">Fermer</button>
        </div>
      </div>
    </div>
  );
}
